import React from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { resetCarouselSession } from 'actions/carouselSession';
import { resetLodgingCheckboxSession } from 'actions/lodgingCheckboxSession';
import useTrackSearchChanged from 'hooks/useTrackSearchChanged';
import CSSVariablesProvider from 'components/CSSVariablesProvider/CSSVariablesProvider';
import SearchWidget from '../../ui/atoms/SearchWidget';
import '../../styles/components/search/SearchForm';

const DATE_FORMAT_REDUX = 'DD-MM-YYYY';

/**
 * Search bar component
 * @param {Object} props - Component props
 * @param {boolean} props.isModalVersion - If the search bar is being used inside a modal.
 * @returns {JSX} - React component
 */
const SearchBar = ({ isModalVersion }) => {
  const dispatch = useDispatch();
  const { env, features } = useSelector((state) => state.whitelabelConfig);

  const { origin, destination, departureDate, returnDate, hybridTrip } = useSelector(
    ({ search }) => {
      const searchWidgetDateFormat = env.searchWidget.dateFormat;

      /**
       * Format the date string to the search widget date format.
       * @param {string} dateString - The date string to format.
       * @returns {string} - The formatted date string.
       */
      const formatDate = (dateString) => {
        if (!dateString) return null;
        return moment(dateString, DATE_FORMAT_REDUX).format(
          searchWidgetDateFormat?.toUpperCase() || 'DD-MMM-YY',
        );
      };

      return {
        origin: search.get('originId'),
        destination: search.get('destinationId'),
        departureDate: formatDate(search.get('departureDate')),
        returnDate: formatDate(search.get('returnDate')),
        hybridTrip: search.get('hybridTrip'),
      };
    },
  );

  const trackSearch = useTrackSearchChanged({ isModalVersion });

  /**
   * Handle the search change event.
   * @param {string} absoluteSearchUrl - The absolute search URL.
   * @returns {void}
   */
  const handleSearchChange = (absoluteSearchUrl) => {
    trackSearch(absoluteSearchUrl);
    dispatch(resetCarouselSession());
    dispatch(resetLodgingCheckboxSession());
  };

  const searchWidget = (
    <SearchWidget
      origin={origin}
      destination={destination}
      departureDate={departureDate}
      returnDate={returnDate}
      hybridTrip={hybridTrip}
      searchCallback={handleSearchChange}
    />
  );

  return isModalVersion ? (
    <CSSVariablesProvider
      theme={env.theme}
      funnelStyle={features.FUNNEL_STYLE}
      funnelStyleBrandVariation={env.brand.toUpperCase()}
    >
      {searchWidget}
    </CSSVariablesProvider>
  ) : (
    <div className="search-form-wrapper">
      <div className="search-form-wrapper-container">{searchWidget}</div>
    </div>
  );
};

SearchBar.propTypes = {
  isModalVersion: PropTypes.bool,
};

export default SearchBar;
